import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";

/**
 * Merge Tailwind CSS class names, resolving conflicts with tailwind-merge.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date value to a human-readable string.
 * @param date - A Date object, timestamp, or ISO date string
 * @param pattern - date-fns format pattern (default: "MMM d, yyyy")
 */
export function formatDate(date: Date | number | string, pattern = "MMM d, yyyy"): string {
  return format(new Date(date), pattern);
}

/**
 * Compact relative-time string for audit cells in tables ("Added 5d
 * ago by Alice"). Uses minute/hour/day granularity for anything in
 * the last 30 days, then falls back to absolute "MMM d" for older
 * timestamps — relative time loses its usefulness past a month
 * ("2mo ago" doesn't tell you which year), and absolute date with
 * no year is the shorthand humans naturally read on dense rows.
 *
 * Inputs:
 *   - ISO 8601 string (what Supabase returns for timestamptz),
 *     Date object, or epoch ms — same input shape as formatDate.
 *
 * Outputs (examples):
 *   "just now"   — <60 seconds
 *   "12m ago"    — minutes
 *   "3h ago"     — hours
 *   "5d ago"     — days, within 30
 *   "Apr 28"     — days, >30 (year omitted; the cell's tooltip
 *                  carries the full timestamp for precision)
 *
 * Server-rendered: produces a stable snapshot at render time. Pages
 * that need it to tick forward should remount the rendered tree on
 * navigation (Next.js's revalidatePath handles this for our actions).
 */
export function formatRelativeTime(date: Date | number | string): string {
  const then = new Date(date).getTime();
  const now = Date.now();
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  // Beyond 30 days: absolute "MMM d". Year omitted for compactness
  // — the cell's title= tooltip carries the full timestamp.
  return format(new Date(date), "MMM d");
}

/**
 * Format a date value with date + time-to-the-minute + timezone, in
 * the viewer's local timezone. Use this on surfaces where "exactly
 * when did this happen" matters — e.g. the upload tracker, where
 * "Apr 28, 2026, 2:32 PM PDT" tells the user the upload was minutes
 * ago, while a date alone ("Apr 28, 2026") tells them roughly nothing
 * about freshness.
 *
 * Implementation note: uses Intl.DateTimeFormat rather than date-fns
 * because the timezone-name token (`zzz`) in date-fns is only
 * supported by the date-fns-tz extension and requires explicit locale
 * plumbing. Intl is built into every runtime we target, picks up the
 * viewer's locale automatically, and emits a sensible localised
 * string with no extra dependency.
 */
export function formatDateTime(date: Date | number | string): string {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(date));
}
